/**
 * Pure summariser for the POST /api/projects/{projectId}/resources/sync-access response.
 *
 * WHY this exists: the "Sync access" button used to toast "Team access synced to Slack, Jira,
 * GitHub and SharePoint" no matter what the server returned — including runs where every invite
 * failed, or where nothing was attempted at all. The backend never 5xx's on an integration failure
 * (partial failures come back as `failed` counts in a 200), so the counts are the ONLY truthful
 * signal and must drive the message.
 *
 * Backend semantics this mirrors (packages/projects/services/provision-resource-access.service.ts):
 *   - 0 assigned resources, unknown project, or an orchestrator-level failure → every counter is 0
 *     (`emptyAccessCounts`). `resource_count` (added to the response for this purpose) tells the
 *     "no team members" case apart from the others.
 *   - An integration that is not linked / not configured (no channel, no repo, no Jira URL, missing
 *     credentials) is left UNTOUCHED — its counters stay 0; it is NOT counted as `skipped`.
 *   - `sharepoint.skipped` is incremented when a site URL exists but the M365 group id does not.
 *   - `clockify.ok` counts people now on the Clockify project (added OR already a member);
 *     `clockify.skipped` counts people with no Clockify user id (T-13b).
 *   - `github.skipped` is declared but not yet incremented server-side; it is still read here so
 *     the message stays correct once it is wired.
 *
 * Messages are plain text — the toast variant is never the only carrier of meaning (WCAG 1.4.1).
 */

export type SyncIntegration =
  | "slack"
  | "jira"
  | "github"
  | "sharepoint"
  | "clockify";

/** Mirrors `AccessCounts` (backend). Every field optional: the client tolerates partial bodies. */
export interface SyncAccessCounts {
  slack?: { ok?: number; failed?: number; cacheHit?: number; resolved?: number };
  jira?: { ok?: number; failed?: number; cacheHit?: number; resolved?: number };
  github?: { ok?: number; failed?: number; skipped?: number };
  sharepoint?: {
    ok?: number;
    failed?: number;
    skipped?: number;
    elevated?: number;
  };
  clockify?: {
    ok?: number;
    failed?: number;
    skipped?: number;
    alreadyMember?: number;
    elevated?: number;
    elevationUnavailable?: number;
  };
  leadership?: number;
}

export type SyncSummaryKind = "success" | "warning" | "info";

export interface SyncSummary {
  kind: SyncSummaryKind;
  message: string;
}

export interface SummariseSyncInput {
  counts: SyncAccessCounts | null | undefined;
  /**
   * Active, email-bearing resources the server attempted to sync. Optional for back-compat with a
   * server that predates the field — the summary then falls back to a less specific message.
   */
  resourceCount?: number | null;
  /** Which integrations are linked on the project (from the Tools card state). Unknown = absent. */
  linked?: Partial<Record<SyncIntegration, boolean>>;
}

const LABELS: Record<SyncIntegration, string> = {
  slack: "Slack",
  jira: "Jira",
  github: "GitHub",
  sharepoint: "SharePoint",
  clockify: "Clockify",
};

/** Display order. Each integration only appears in the message when its counts are non-zero. */
const ORDER: SyncIntegration[] = [
  "slack",
  "jira",
  "github",
  "sharepoint",
  "clockify",
];

function n(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : 0;
}

/** "A", "A and B", "A, B and C". */
export function joinList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

interface Tally {
  tool: SyncIntegration;
  ok: number;
  failed: number;
  skipped: number;
}

function tally(counts: SyncAccessCounts, tool: SyncIntegration): Tally {
  const c = (counts[tool] ?? {}) as {
    ok?: number;
    failed?: number;
    skipped?: number;
  };
  return { tool, ok: n(c.ok), failed: n(c.failed), skipped: n(c.skipped) };
}

/** Turn the sync-access response into one accurate, human-readable toast. Pure. */
export function summariseSyncAccess({
  counts,
  resourceCount,
  linked = {},
}: SummariseSyncInput): SyncSummary {
  if (resourceCount === 0) {
    return {
      kind: "info",
      message:
        "No team members to sync — assign team members with an email address first.",
    };
  }

  const tallies = ORDER.map((tool) => tally(counts ?? {}, tool));

  const failures = tallies.filter((t) => t.failed > 0);
  // An integration with only successes (no failures) is listed as a success.
  const successes = tallies.filter((t) => t.ok > 0 && t.failed === 0);
  const skipped = tallies.filter((t) => t.skipped > 0);

  const successText = successes.map((t) => `${LABELS[t.tool]} ${t.ok}`);
  const skippedText = skipped.map(
    (t) => `${LABELS[t.tool]}: ${t.skipped} skipped`,
  );

  if (failures.length > 0) {
    const failureText = failures.map(
      (t) => `${LABELS[t.tool]}: ${t.failed} of ${t.ok + t.failed} failed`,
    );
    const parts = [`Access sync finished with errors. ${failureText.join("; ")}.`];
    if (successText.length > 0) parts.push(`Synced: ${successText.join(", ")}.`);
    if (skippedText.length > 0) parts.push(`${skippedText.join("; ")}.`);
    return { kind: "warning", message: parts.join(" ") };
  }

  if (successes.length > 0) {
    const suffix =
      skippedText.length > 0 ? ` (${skippedText.join("; ")})` : "";
    return {
      kind: "success",
      message: `Access synced: ${successText.join(", ")}${suffix}`,
    };
  }

  // Nothing succeeded and nothing failed — explain why instead of claiming success.
  if (skippedText.length > 0) {
    return {
      kind: "info",
      message: `Nothing synced — ${skippedText.join("; ")}.`,
    };
  }

  const notLinked = ORDER.filter((tool) => linked[tool] === false).map(
    (tool) => LABELS[tool],
  );
  if (notLinked.length > 0) {
    return {
      kind: "info",
      message: `Nothing synced — ${joinList(notLinked)} ${notLinked.length === 1 ? "is" : "are"} not linked.`,
    };
  }

  if (resourceCount === undefined || resourceCount === null) {
    return {
      kind: "info",
      message:
        "Nothing synced — no team members with an email are assigned, or no integrations are linked.",
    };
  }

  return {
    kind: "info",
    message:
      "Nothing synced — the linked integrations did not accept any changes. Check the integration setup and try again.",
  };
}
